import { Items } from '@prisma/client';
import API_URL from '../apiconfig';
import {
  ItemApiPayload,
  ItemWithCommissions,
  SearchItemsPage,
} from '../types/items';
import axios from './api';
import client from '../utils/react-query/client';
import { uploadFiles } from './global';
import { ImageItem } from '../utils/form/appointmentForm';
import { stripStorageToken } from '../helpers/global';

export async function getItems({
  businessId,
  includeDeleted,
}: {
  businessId: string;
  includeDeleted?: boolean;
}) {
  try {
    const response = await axios.get(`${API_URL}/v1/items`, {
      params: {
        businessId,
        includeDeleted,
      },
    });

    return response.data.result as ItemWithCommissions[];
  } catch (error) {
    throw new Error('Failed to get items', { cause: error });
  }
}

export async function searchItems({
  businessId,
  query,
  includeDeleted,
  cursor,
  orderByFavoritedAt,
  includeGiftCards,
  giftCardsOnly,
}: {
  businessId: string;
  query: string;
  includeDeleted?: boolean;
  cursor?: string;
  orderByFavoritedAt?: boolean;
  includeGiftCards?: boolean;
  giftCardsOnly?: boolean;
}) {
  try {
    const response = await axios.get(`${API_URL}/v1/items/search`, {
      params: {
        businessId,
        includeDeleted,
        includeGiftCards,
        giftCardsOnly,
        query,
        cursor: cursor || undefined,
        orderByFavoritedAt,
      },
    });

    return response.data as SearchItemsPage;
  } catch (error) {
    throw new Error('Failed to get items', { cause: error });
  }
}

export async function createItem({
  businessId,
  payload,
}: {
  businessId: string;
  payload: ItemApiPayload;
}) {
  try {
    const response = await axios.post(`${API_URL}/v1/items`, {
      businessId,
      ...payload,
    });

    return response.data.result as Items;
  } catch (error) {
    throw new Error('Failed to create item', { cause: error });
  }
}

export async function updateItem({
  itemId,
  businessId,
  payload,
}: {
  itemId: string;
  businessId: string;
  payload: ItemApiPayload;
}) {
  try {
    const response = await axios.patch(`${API_URL}/v1/items/${itemId}`, {
      ...payload,
      businessId,
    });

    return response.data.result as Items;
  } catch (error) {
    throw new Error('Failed to update item', { cause: error });
  }
}

export async function invalidateItems() {
  await client.invalidateQueries({
    queryKey: ['retail-items'],
    exact: false,
  });
}

export async function deleteItem({ itemId }: { itemId: string }) {
  try {
    const response = await axios.delete(`${API_URL}/v1/items/${itemId}`);

    return response.data.result as boolean;
  } catch (error) {
    throw new Error('Failed to delete item', { cause: error });
  }
}

export async function invalidateSearchItems() {
  await client.invalidateQueries({
    queryKey: ['seach-retail-items'],
    exact: false,
  });
}

export async function resetSearchItems() {
  await client.resetQueries({
    queryKey: ['seach-retail-items'],
    exact: false,
  });
}

export async function uploadItemImages(
  images: ImageItem[],
  businessId: string,
) {
  const urls = await uploadFiles(
    images.map(img => ({
      id: img.id,
      url: img.url,
      // keep in sync with persistTempItemImages in api/src/helpers/items.ts; the uuid stops a move overwriting another photo
      path: `tempImages/items-${businessId}-${Date.now()}_${crypto.randomUUID()}`,
    })),
    {
      onSuccess: img => {
        URL.revokeObjectURL(img.url);
      },
    },
  );
  return urls.map(stripStorageToken);
}
